// PR-MAY16-INVESTOR-RATES-V2 item 5 — conflict queue hooks.
//
// Conflicts are logged by migration 079 when the auto-promote step
// finds >1 distinct rate per (contract, investor, effective_from)
// tuple. Today's data: zero conflicts; the queue is scaffolding for
// future operator overrides.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface InvestorRateConflictRow {
  id: string;
  site_id: string;
  site_name: string;
  investor_id: string;
  investor_name: string;
  effective_from: string;
  distinct_rates: Array<{ rate: number | string; count: number }>;
  vehicle_count: number;
  resolved_at: string | null;
  resolved_rate: string | null;
}

export function useInvestorRateConflicts(includeResolved = false) {
  return useQuery({
    queryKey: ['investor-rate-conflicts', includeResolved],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: InvestorRateConflictRow[] }>(
        `/api/v1/investor-rates/conflicts${includeResolved ? '?open=false' : ''}`,
      );
      return data.items;
    },
  });
}

export function useResolveInvestorRateConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, resolvedRate, notes }: { id: string; resolvedRate: number; notes?: string | null }) => {
      const { data } = await apiClient.post<{ conflict: InvestorRateConflictRow }>(
        `/api/v1/investor-rates/conflicts/${id}/resolve`,
        { resolvedRate, notes: notes ?? null },
      );
      return data.conflict;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-rate-conflicts'] });
      qc.invalidateQueries({ queryKey: ['investor-site-rates'] });
    },
  });
}
